import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  alpha,
  useTheme,
  IconButton
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Visibility, 
  Campaign as CampaignIcon, 
  CalendarToday, 
  EmojiEvents,
  Store,
  Business
} from '@mui/icons-material';
import { campaignService } from '../services/campaignService';
import { useAuth } from '../contexts/AuthContext';
import { Campaign } from '../types';
import { AppHeader } from '../components/AppHeader';
import { CampaignImage } from '../components/CampaignImage';

const Campaigns: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; campaign: Campaign | null }>({
    open: false,
    campaign: null
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      // Para USUARIO_FINAL, carregar apenas campanhas ativas
      const data = user?.role === 'USUARIO_FINAL'
        ? await campaignService.getActiveCampaigns()
        : await campaignService.getCampaignsByAssociation();
      
      setCampaigns(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleDelete = async (campaign: Campaign) => {
    try {
      await campaignService.deleteCampaign(campaign.id);
      setDeleteDialog({ open: false, campaign: null });
      fetchCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir campanha');
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'default';
      case 'COMPLETED':
        return 'info';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Ativa';
      case 'DRAFT':
        return 'Rascunho';
      case 'COMPLETED':
        return 'Concluída';
      case 'CANCELLED':
        return 'Cancelada';
      default:
        return 'Desconhecido';
    }
  };

  // Layout em Cards para USUARIO_FINAL
  const renderCardLayout = () => (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
        minHeight: '100vh',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Carregando campanhas...
            </Typography>
          </Box>
        ) : campaigns.length === 0 ? (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
            }}
          >
            <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Nenhuma campanha ativa no momento
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Fique atento! Novas campanhas serão lançadas em breve.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 4 }}>
            {campaigns.map((campaign) => (
              <Box key={campaign.id}>
                <Card 
                  elevation={3}
                  sx={{ 
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  {/* Imagem da Campanha */}
                  <CampaignImage
                    imageUrl={campaign.imageUrl}
                    alt={campaign.name}
                    width="100%"
                    height="auto"
                    objectFit="contain"
                  />

                  <CardContent sx={{ p: 3 }}>
                    {/* Status Chip */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={getStatusLabel(campaign.status)}
                        color={getStatusColor(campaign.status) as any}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <CalendarToday fontSize="small" />
                        <Typography variant="caption">
                          {new Date(campaign.endDate).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Título e Descrição */}
                    <Typography variant="h6" fontWeight={700} gutterBottom color="primary.main">
                      {campaign.name}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {campaign.description || 'Descrição não disponível'}
                    </Typography>

                    {/* Informações Rápidas */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmojiEvents color="warning" fontSize="small" />
                        <Typography variant="caption" fontWeight={600}>
                          {campaign.prizes?.length || 0} prêmios
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Store color="primary" fontSize="small" />
                        <Typography variant="caption" fontWeight={600}>
                          {campaign.stores?.length || 0} lojas
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business color="secondary" fontSize="small" />
                        <Typography variant="caption" fontWeight={600}>
                          {campaign.sponsors?.length || 0} patrocinadores
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/campaigns/${campaign.id}`)}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '1rem',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                        }
                      }}
                    >
                      Ver Detalhes
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );

  // Layout em Tabela para ASSOCIACAO
  const renderTableLayout = () => (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
          Campanhas
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/campaigns/new')}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Nova Campanha
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Data Início</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Data Fim</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'primary.contrastText' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <CampaignIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Nenhuma campanha encontrada
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Crie sua primeira campanha para começar
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate('/campaigns/new')}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Criar Primeira Campanha
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{campaign.description || 'Sem descrição'}</TableCell>
                  <TableCell>
                    {new Date(campaign.startDate).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    {new Date(campaign.endDate).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(campaign.status)}
                      color={getStatusColor(campaign.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, campaign })}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );

  return (
    <>
      {user?.role === 'ASSOCIACAO' && (
        <AppHeader 
          title="Gerenciar Campanhas" 
          subtitle="Crie, edite e administre campanhas promocionais"
          icon={<CampaignIcon />}
        />
      )}

      {user?.role === 'USUARIO_FINAL' && (
        <AppHeader 
          title="Campanhas Promocionais" 
          subtitle="Participe de nossas promoções e concorra a prêmios incríveis!"
          icon={<EmojiEvents />}
        />
      )}

      {user?.role === 'USUARIO_FINAL' 
        ? renderCardLayout() 
        : renderTableLayout()
      }

      {/* Dialog de confirmação para exclusão */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, campaign: null })}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a campanha "{deleteDialog.campaign?.name}"? 
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, campaign: null })}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => deleteDialog.campaign && handleDelete(deleteDialog.campaign)}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Campaigns;